import {
    AlignmentConfig,
    DEFAULT_ALIGNMENT,
    DEFAULT_OFFSET,
    DEFAULT_PADDING,
    HorizontalAlignment,
    Padding,
    Position,
    VerticalAlignment
} from "./layout-manager.models";

/**
 * Serviço que disponibiliza funções auxiliares para criações de Layouts com o LayoutManager.
 */
export class LayoutUtils {

    constructor() {
        // Construtor intencionalmente vazio
    }

    /**
     * Recebe um objeto de AlignmentConfig parcialmente preenchido e inicializa todas as propriedades.
     * Se o objeto for null/undefined, retorna o alinhamento padrão.
     *
     * @param padding objeto de Padding parcialmente preenchido.
     *
     * @returns um objeto padding completamente preenchido.
     */
    static initializeAlignment(alignment: Partial<AlignmentConfig>): AlignmentConfig {
        if (alignment) {
            const horizontal = alignment.horizontal ?? HorizontalAlignment.LEFT;
            const vertical   = alignment.vertical   ?? VerticalAlignment.TOP;

            return { horizontal, vertical }
        }

        return DEFAULT_ALIGNMENT;
    }

    /**
     * Recebe um objeto de Position parcialmente preenchido e inicializa todas as propriedades.
     * Se o objeto for null/undefined, retorna o offset padrão.
     *
     * @param padding objeto de Padding parcialmente preenchido.
     *
     * @returns um objeto padding completamente preenchido.
     */
    static initializeOffset(offset: Partial<Position>): Position {
        if (offset) {
            const x = offset.x ?? 0;
            const y = offset.y ?? 0;

            return { x, y }
        }

        return DEFAULT_OFFSET;
    }

    /**
     * Recebe um objeto de Padding parcialmente preenchido e inicializa todas as propriedades.
     * Se o objeto for null/undefined, retorna o padding padrão.
     *
     * @param padding objeto de Padding parcialmente preenchido.
     *
     * @returns um objeto padding completamente preenchido.
     */
    static initializePadding(padding: Partial<Padding>): Padding {
        if (padding) {
            const left   = padding.left   ?? 0;
            const right  = padding.right  ?? 0;
            const top    = padding.top    ?? 0;
            const bottom = padding.bottom ?? 0;

            return { left, right, top, bottom }
        }

        return DEFAULT_PADDING;
    }

    /**
     * Cria um objeto de padding com todas as propriedades definidas igualmente.
     *
     * @param value tamanho do padding a ser definido em cada propriedade.
     *
     * @returns um objeto padding.
     */
    static createEqualPadding(value: number): Padding {
        return {
            left:   value,
            right:  value,
            top:    value,
            bottom: value
        }
    }

    /**
     * Cria um objeto de padding a partir de tamanhos horizontais e verticais.
     *
     * @param horizontal tamanho do padding a ser definido nas propriedades horizontais (i.e. left, right).
     * @param vertical tamanho do padding a ser definido nas propriedades verticais (i.e. top, bottom).
     *
     * @returns um objeto padding.
     */
    static createPaddingByAxis(horizontal: number, vertical: number): Padding {
        return {
            left:   horizontal,
            right:  horizontal,
            top:    vertical,
            bottom: vertical
        }
    }
}
